import { createContext, useCallback, useContext, useEffect, useRef, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, EmptyState, PageLoading } from '@/components';
import KioskLayout from '@/layouts/KioskLayout';
import { useAuth } from '@/contexts/Authentication';
import { ApiError } from '@/services/api';
import { getCabinStatus, sendHeartbeat, totemLogout, type CabinStatus } from '../services/calls/totem';
import { HEARTBEAT_SECONDS, readTotemToken } from '../constants/storage';
import '../cabin.css';

/**
 * Casca do totem (/cabine/:subdominio/*): identifica a cabine pelo token do dispositivo, envia o heartbeat
 * (RN-102) e oferece `exit()` — encerra a sessão do paciente e volta à tela inicial (reset do totem).
 */
interface CabinCtx { sub: string; token: string; cabin: CabinStatus; exit: () => Promise<void>; refresh: () => void }
const Ctx = createContext<CabinCtx | null>(null);
export const useCabin = () => {
  const c = useContext(Ctx);
  if (!c) throw new Error('useCabin fora do CabinShell');
  return c;
};
export const cabinStatusKey = (sub: string) => ['cabin', 'status', sub];

export default function CabinShell() {
  const { subdominio = '' } = useParams();
  const { pathname } = useLocation();
  const token = readTotemToken(subdominio);
  if (pathname.endsWith('/configurar')) return <Outlet />;
  if (!token) {
    return (
      <KioskLayout>
        <EmptyState icon="🔧" title="Totem não configurado">
          <p>Este equipamento ainda não tem o token da cabine. Peça o token à gestão Azanas.</p>
          <Link className="btn" to={`/cabine/${subdominio}/configurar`}>Configurar totem</Link>
        </EmptyState>
      </KioskLayout>
    );
  }
  return <ShellWithToken sub={subdominio} token={token} />;
}

function ShellWithToken({ sub, token }: { sub: string; token: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { setMe } = useAuth();
  const status = useQuery({ queryKey: cabinStatusKey(sub), queryFn: () => getCabinStatus(token), refetchInterval: 60_000, retry: 1 });

  // heartbeat: 1ª camada de detecção de queda (RN-102)
  useEffect(() => {
    const beat = () => sendHeartbeat(token).then((s) => qc.setQueryData(cabinStatusKey(sub), s)).catch(() => {});
    void beat();
    const t = setInterval(beat, HEARTBEAT_SECONDS * 1000);
    return () => clearInterval(t);
  }, [token, sub, qc]);

  const exiting = useRef(false);
  const exit = useCallback(async () => {
    if (exiting.current) return;
    exiting.current = true;
    await totemLogout().catch(() => {});
    qc.removeQueries({ queryKey: ['care'] });
    setMe(null);
    navigate(`/cabine/${sub}`, { replace: true });
    exiting.current = false;
  }, [qc, setMe, navigate, sub]);

  if (status.isLoading) return <KioskLayout><PageLoading /></KioskLayout>;
  if (status.error || !status.data) {
    const invalid = status.error instanceof ApiError && status.error.status === 401;
    return (
      <KioskLayout>
        <EmptyState icon={invalid ? '🔑' : '📡'} title={invalid ? 'Token do totem inválido' : 'Cabine temporariamente indisponível.'}>
          <p>{invalid ? 'Peça um novo token à gestão Azanas e configure novamente.' : 'Verificando a conexão… tente novamente em instantes.'}</p>
          {invalid ? <Link className="btn" to={`/cabine/${sub}/configurar`}>Configurar totem</Link> : <Button variant="ghost" onClick={() => status.refetch()}>Tentar novamente</Button>}
        </EmptyState>
      </KioskLayout>
    );
  }
  return (
    <Ctx.Provider value={{ sub, token, cabin: status.data, exit, refresh: () => void status.refetch() }}>
      <Outlet />
    </Ctx.Provider>
  );
}

/** Reinicia o totem após inatividade (toques/teclas reiniciam a contagem). */
export function useIdleReset(seconds: number, onIdle: () => void, enabled = true) {
  const cb = useRef(onIdle);
  cb.current = onIdle;
  useEffect(() => {
    if (!enabled) return;
    let t = setTimeout(() => cb.current(), seconds * 1000);
    const reset = () => { clearTimeout(t); t = setTimeout(() => cb.current(), seconds * 1000); };
    const events = ['pointerdown', 'keydown', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => { clearTimeout(t); events.forEach((e) => window.removeEventListener(e, reset)); };
  }, [seconds, enabled]);
}

/** Barra do totem com o contexto à direita. */
export function CabinFrame({ right, children }: { right?: ReactNode; children: ReactNode }) {
  return <KioskLayout right={right}>{children}</KioskLayout>;
}
